import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { MeteorObservable } from 'meteor-rxjs';
import { BehaviorSubject, combineLatest, map, Subscription } from 'rxjs';
import { Customer } from 'server/shared/models/customer';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';
import { TranslationService } from '../../services/translation.service';

type CustomerItem = { id: string; displayName: string };

@Component({
  selector: 'app-customer-select',
  templateUrl: './customer-select.component.html',
  styleUrls: ['./customer-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerSelectComponent implements OnInit, OnDestroy {
  sharedPoolCustomerSubscription?: Subscription;

  @Output() selectionChanged = new EventEmitter<CustomerItem[]>();

  disabled$ = new BehaviorSubject<boolean>(false);
  @Input() set disabled(value: boolean) {
    this.disabled$.next(value);
  }
  get disabled() {
    return this.disabled$.value;
  }

  showHint$ = new BehaviorSubject<boolean>(false);
  @Input() set showHint(value: boolean) {
    this.showHint$.next(value);
  }
  get showHint() {
    return this.showHint$.value;
  }

  selectedCustomers$ = new BehaviorSubject<CustomerItem[]>([]);
  @Input() set selectedCustomers(customers: CustomerItem[]) {
    this.selectedCustomers$.next(customers);
  }

  getParameterSeparator = ',';
  @Input() useQueryParam: string | false = false;

  @Input() set restrictToCustomerIds(customerIds: string[]) {
    this._restrictToCustomerIds$.next(customerIds);
  }
  _restrictToCustomerIds$ = new BehaviorSubject<string[] | undefined>(
    undefined,
  );

  @Input() includeTransferCustomers: boolean = false;

  i18n = this.translationService.getTranslations('jobs');

  loggedInCustomer$ = this.customerService.readyDispoCustomer$;

  allCustomers$ = combineLatest([
    this.loggedInCustomer$,
    this.customerService.sharedPoolCustomers$,
  ]).pipe(
    map(([loggedInCustomer, sharedPoolCustomers]) => {
      let ownCustomer = { ...loggedInCustomer };
      ownCustomer.name = this.i18n?.internalPoolers || 'Interne Pooler';
      let customers: Customer[] = [ownCustomer, ...(sharedPoolCustomers || [])];
      let transferCustomers: CustomerItem[] = [];
      if (customers) {
        this.getInitialQueryParams(customers);
      }
      let customerItems: CustomerItem[] = customers.map((customer) => ({
        id: customer._id,
        displayName: customer.name,
      }));
      if (this.includeTransferCustomers) {
        ownCustomer.customerTransfers?.transfers?.forEach((transfer) => {
          transferCustomers.push({
            id: transfer.targetCustomer.id,
            displayName: transfer.targetCustomer.displayName,
          });
        });
        ownCustomer.customerTransfers?.employmentTransfers?.forEach(
          (transfer) => {
            transferCustomers.push({
              id: transfer.targetCustomer.id,
              displayName: transfer.targetCustomer.displayName,
            });
          },
        );
        let allCustomerItems = [...customerItems, ...transferCustomers];
        let uniqueCustomerIds: CustomerItem[] = [];
        allCustomerItems.forEach((customer) => {
          if (!uniqueCustomerIds.some((c) => c.id === customer.id)) {
            uniqueCustomerIds.push(customer);
          }
        });
        customerItems = uniqueCustomerIds;
      }
      return customerItems;
    }),
  );
  searchText$ = new BehaviorSubject<string>('');
  filteredCustomers$ = combineLatest([
    this.allCustomers$,
    this.searchText$,
    this._restrictToCustomerIds$,
    this.selectedCustomers$,
  ]).pipe(
    map(
      ([customers, searchText, _restrictToCustomerIds, selectedCustomers]) => {
        let filteredCustomers = customers || [];
        if (selectedCustomers.length) {
          filteredCustomers = filteredCustomers.filter(
            (customer) =>
              !selectedCustomers.some(
                (selectedCustomer) => selectedCustomer.id === customer.id,
              ),
          );
        }
        if (_restrictToCustomerIds) {
          filteredCustomers = filteredCustomers.filter((customer) =>
            _restrictToCustomerIds.includes(customer.id),
          );
        }
        if (searchText) {
          filteredCustomers = filteredCustomers.filter((customer) =>
            customer.displayName
              .toLowerCase()
              .includes(searchText.toLowerCase()),
          );
        }
        return filteredCustomers;
      },
    ),
    rxNgZoneRun(this.ngZone),
  );

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService,
    private router: Router,
    private route: ActivatedRoute,
    private customerService: CustomerService,
  ) {}

  ngOnInit(): void {
    this.sharedPoolCustomerSubscription = MeteorObservable.subscribe(
      'sharedPoolCustomers',
    ).subscribe();
  }

  ngOnDestroy() {
    if (this.sharedPoolCustomerSubscription) {
      this.sharedPoolCustomerSubscription.unsubscribe();
    }
  }

  emitValue(updateQueryParams = true) {
    if (this.useQueryParam && updateQueryParams) {
      this.updateQueryParams({
        [this.useQueryParam]:
          this.selectedCustomers$.value
            .map((customer) => customer.id)
            .join(this.getParameterSeparator) || '',
      });
    }
    this.selectionChanged.emit(this.selectedCustomers$.value);
  }

  searchCustomer(searchText: string | CustomerItem) {
    if (typeof searchText !== 'string') {
      searchText = '';
    }
    this.searchText$.next(searchText);
  }

  getInitialQueryParams(customers: Customer[]): void {
    let queryParams = this.route.snapshot.queryParams;
    if (this.useQueryParam && queryParams[this.useQueryParam] !== undefined) {
      const customerIds = queryParams[this.useQueryParam].split(
        this.getParameterSeparator,
      );
      this.selectedCustomers$.next(
        customers
          .filter((customer) => customerIds.includes(customer._id))
          .map((customer) => ({
            id: customer._id,
            displayName: customer.name,
          })),
      );
      this.emitValue(false);
      this.searchText$.next('');
    }
  }

  selectedCustomer(matSelectedEvent: MatAutocompleteSelectedEvent) {
    const customer = matSelectedEvent.option.value;
    const current = this.selectedCustomers$.value;
    current.push(customer);
    this.selectedCustomers$.next(current);
    this.emitValue();
    this.searchText$.next('');
  }

  removeCustomer(customerId: string) {
    this.selectedCustomers$.next(
      this.selectedCustomers$.value.filter(
        (customer) => customer.id !== customerId,
      ),
    );
    this.emitValue();
    this.searchText$.next('');
  }
  private updateQueryParams(queryParams: { [key: string]: string }) {
    this.ngZone.run(() => {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        })
        .then();
    });
  }

  trackByCustomerId(index: number, customer: CustomerItem): string {
    return customer.id;
  }
}
